// AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../config/axiosInstance";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
} from "chart.js";
import { sumObjectValues } from "../../../Components/Dashboard/dashboardShared";
import StatsCards from "../../../Components/Dashboard/StatsCards";
import Charts from "../../../Components/Dashboard/Charts";
import RecentActivity from "../../../Components/Dashboard/RecentActivity";
import SupplierDashboard from "./SupplierDashboard";
import SubAdminDashboard from "./SubAdminDashboard";

// ----- Chart.js registration -----
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement
);

// This route (/admin/dashboard) is shared by every role that can log into
// the admin panel. It used to render the same platform-wide "Admin
// Analytics" view for suppliers AND subadmins (sales executive / graphic
// designer) too — leaking every other user's data to roles that should only
// see their own scope. Now: admin gets the full view, subadmin gets
// SubAdminDashboard (scoped to orders assigned to them), everyone else
// (supplier, offline_supplier) gets SupplierDashboard.
// (The dashboard-reports API is now locked down to match — see
// dashboardReportsRoutes.js — so this is UI-level, not the only guard.)
// This has to stay a plain role check with no hooks of its own — AdminStats
// below calls hooks unconditionally, so the branch must happen a component
// higher, not via an early return inside a component that also uses hooks.
export default function AdminDashboard() {
  let userType = null;
  try {
    userType = JSON.parse(localStorage.getItem("user") || "{}")?.user_type;
  } catch {
    userType = null;
  }

  if (userType === "admin") return <AdminStats />;
  if (userType === "subadmin") return <SubAdminDashboard />;
  return <SupplierDashboard />;
}

// ---------- main (admin/subadmin) ----------
function AdminStats() {
  const baseURL = process.env.REACT_APP_URL;

  // payload states
  const [dash, setDash] = useState(null); // /dashboard-stats
  const [usersReport, setUsersReport] = useState(null); // /user-reports
  const [ordersReport, setOrdersReport] = useState(null); // /order-reports
  const [productsReport, setProductsReport] = useState(null); // /product-reports

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // fetch all
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        const [d1, d2, d3, d4] = await Promise.all([
          axiosInstance.get(`${baseURL}/admin/dashboard-reports/dashboard-stats`),
          axiosInstance.get(`${baseURL}/admin/dashboard-reports/user-reports`),
          axiosInstance.get(`${baseURL}/admin/dashboard-reports/order-reports`),
          axiosInstance.get(`${baseURL}/admin/dashboard-reports/product-reports`),
        ]);

        if (!alive) return;
        setDash(d1?.data?.data || null);
        setUsersReport(d2?.data?.data || null);
        setOrdersReport(d3?.data?.data || null);
        setProductsReport(d4?.data?.data || null);
        setErr("");
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [baseURL]);

  // derived values
  const totalUsers = useMemo(() => sumObjectValues(dash?.userStats), [dash]);
  const totalOrders = useMemo(() => sumObjectValues(dash?.orderStats), [dash]);

  const recentUsers = useMemo(
    () => (usersReport?.users || []).slice(0, 6),
    [usersReport]
  );
  const recentOrders = useMemo(
    () => (ordersReport?.orders || []).slice(0, 6),
    [ordersReport]
  );
  // Export the current dashboard metrics to CSV (dynamic, from live data)
  const handleExport = () => {
    const rows = [["Metric", "Value"]];
    rows.push(["Total Users", totalUsers]);
    Object.entries(dash?.userStats || {}).forEach(([k, v]) =>
      rows.push([`Users — ${k}`, v])
    );
    rows.push(["Total Orders", totalOrders]);
    Object.entries(dash?.orderStats || {}).forEach(([k, v]) =>
      rows.push([`Orders — ${k.replaceAll("_", " ")}`, v])
    );
    rows.push(["Active Products", dash?.productStats?.active || 0]);
    rows.push(["Total Revenue", dash?.totalRevenue || 0]);
    rows.push(["New Users (7d)", dash?.recentActivity?.newUsers || 0]);
    rows.push(["New Orders (7d)", dash?.recentActivity?.newOrders || 0]);
    rows.push(["New Products (7d)", dash?.recentActivity?.newProducts || 0]);

    const csv = rows
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // loading / error states
  if (loading) {
    return (
      <div className="dashboard">
        <header className="dashboard-header">
          <div className="container">
            <h1 className="dashboard-title">Admin Analytics</h1>
            <div className="live-dot">
              <span></span>Live
            </div>
          </div>
        </header>
        <main className="container">
          <div className="skeleton skeleton__title" />
          <div className="grid grid--four">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton skeleton__card" />
            ))}
          </div>
          <div className="grid grid--three mt-24">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton skeleton__panel" />
            ))}
          </div>
        </main>
        <DashboardStyles />
      </div>
    );
  }

  if (err) {
    return (
      <div className="dashboard">
        <header className="dashboard-header">
          <div className="container">
            <h1 className="dashboard-title">Admin Analytics</h1>
            <div className="live-dot">
              <span></span>Live
            </div>
          </div>
        </header>
        <main className="container">
          <div className="alert alert--danger">
            <p className="alert__title">Error loading dashboard</p>
            <p className="alert__text">{err}</p>
          </div>
        </main>
        <DashboardStyles />
      </div>
    );
  }

  // ---------- UI ----------
  return (
    <div className="dashboard">
      {/* Page header */}
      <header className="dashboard-header">
        <div className="container">
          <div className="dashboard-header__main">
            <div className="dashboard-title-row">
              <h1 className="dashboard-title">Admin Analytics</h1>
              <span className="live-dot">
                <span></span>Live
              </span>
            </div>
            <p className="dashboard-subtitle">
              Welcome back — here's what's happening across your packaging
              marketplace.
            </p>
          </div>

          <div className="dashboard-header__actions">
            <button type="button" className="dash-btn" onClick={handleExport}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
          </div>
        </div>
      </header>

      <main className="container">
        {/* KPI cards */}
        <StatsCards
          dash={dash}
          totalUsers={totalUsers}
          totalOrders={totalOrders}
        />

        {/* User registration trend + order pipeline */}
        <Charts
          dash={dash}
          usersReport={usersReport}
          productsReport={productsReport}
          part="top"
        />

        {/* Recent users + recent orders */}
        <RecentActivity
          recentUsers={recentUsers}
          recentOrders={recentOrders}
          part="tables"
        />

        {/* Products by category donut + orders by status */}
        <Charts
          dash={dash}
          usersReport={usersReport}
          productsReport={productsReport}
          part="bottom"
        />

        <footer className="footer">
          Updated {new Date().toLocaleString("en-IN")} · MyPackGenie Admin
          Console
        </footer>
      </main>

      <DashboardStyles />
    </div>
  );
}

// ---------- Styles (one-file, fully theme-aware via design tokens) ----------
function DashboardStyles() {
  return (
    <style>{`
/* All colors come from the global design system tokens (designSystem.css),
   so the dashboard themes automatically in both Light and Dark mode. */
.dashboard{ min-height: 100%; color: var(--color-text-primary); }
.dashboard *{ box-sizing:border-box; }

.dashboard .container{ max-width:100%; margin:0 auto; padding: 0; }
.dashboard .section{ margin: 16px 0; }
.dashboard .section--bottom{ margin-bottom: 32px; }

@keyframes dashPulseDot{ 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
@keyframes dashGrowBar{ from{ transform: scaleY(0); } to{ transform: scaleY(1); } }

/* Page header */
.dashboard-header{ margin-bottom: 22px; }
.dashboard-header .container{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.dashboard-title-row{ display:flex; align-items:center; gap:10px; }
.dashboard-title{
  margin:0; font-size: 24px; font-weight: 800; letter-spacing:-.025em; color: var(--color-text-primary);
}
.dashboard-subtitle{ margin:5px 0 0; font-size:13.5px; color: var(--color-text-secondary); }
.dashboard-header__actions{ display:flex; align-items:center; gap:9px; }
.dash-chip{
  display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 12px; border-radius:10px;
  border:1px solid var(--color-border); background:var(--color-surface);
  font-size:12.5px; font-weight:600; color:var(--color-text-secondary);
}
.dash-btn{
  display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px;
  border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary);
  font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; transition:background .15s ease;
}
.dash-btn:hover{ background:var(--color-hover); }
/* donut center overlay */
.donut-center{
  position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center;
  pointer-events:none;
}
.donut-center .num{ font-size:22px; font-weight:800; font-family:var(--font-mono); color:var(--color-text-primary); }
.donut-center .lbl{ font-size:10px; color:var(--color-text-tertiary); font-weight:600; }
.live-dot{
  display:inline-flex; align-items:center; gap:6px; height:23px; padding:0 9px; border-radius:7px;
  background: var(--color-success-bg); color: var(--color-success-text); font-size:11px; font-weight:700;
}
.live-dot span{ width:6px; height:6px; border-radius:50%; background: var(--color-success-text); animation: dashPulseDot 1.8s infinite; }

/* Cards / panels */
.card{
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}
.card--hover{ transition: box-shadow .2s ease, transform .12s ease; }
.card--hover:hover{ box-shadow: var(--shadow-card-hover); }
.card__header{
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:16px 18px; border-bottom:1px solid var(--color-border-soft);
}
.card__title{ margin:0; font-size: 14.5px; font-weight: 700; color: var(--color-text-primary); }
.card__sub{ margin:3px 0 0; font-size:12px; color: var(--color-text-secondary); }
.card__body{ padding: 18px; }
.card--stretch{ height: 100%; }
.card__link{ font-size:11px; font-weight:700; color: var(--color-primary); cursor:pointer; text-decoration:none; }
.card__link:hover{ text-decoration:underline; }

/* KPI stat cards */
.stat-card{ padding: 18px; }
.stat-card__top{ display:flex; align-items:center; justify-content:space-between; }
.stat-card__title{ margin:0; font-size: 11.5px; font-weight:700; letter-spacing:.04em; text-transform: uppercase; color: var(--color-text-tertiary); }
.stat-card__icon{ width:34px; height:34px; border-radius:10px; display:inline-flex; align-items:center; justify-content:center; flex:0 0 34px; }
.stat-card__row{ display:flex; align-items:baseline; gap:9px; margin-top:12px; }
.stat-card__value{ margin:0; font-size: 30px; line-height: 1; font-weight: 800; letter-spacing:-.03em; color: var(--color-text-primary); font-family: var(--font-mono); }
.stat-card__delta{ display:inline-flex; align-items:center; gap:3px; font-size:11.5px; font-weight:700; }
.stat-card__delta--up{ color: var(--color-success-text); }
.stat-card__hint{ font-size:11.5px; font-weight:600; color: var(--color-text-tertiary); }
.stat-card__note{ margin:11px 0 0; font-size:11.5px; color: var(--color-text-secondary); }
.stat-card__tags{ margin-top:10px; display:flex; flex-wrap:wrap; gap:5px; }
.stat-tag{ font-size:10.5px; font-weight:600; color: var(--color-text-secondary); background: var(--color-surface-2); border:1px solid var(--color-border-soft); border-radius:6px; padding:2px 7px; }
/* mini progress bar (orders split) */
.stat-bar{ margin-top:14px; height:6px; border-radius:4px; background: var(--color-surface-2); overflow:hidden; display:flex; }
.stat-bar > span{ display:block; height:100%; }
.stat-legend{ margin-top:8px; display:flex; gap:12px; font-size:11px; color: var(--color-text-secondary); }
.stat-legend span{ display:flex; align-items:center; gap:5px; }
.stat-legend i{ width:7px; height:7px; border-radius:2px; display:inline-block; }
.stat-pill{ display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:700; border-radius:6px; padding:3px 8px; }

/* KPI big number (7d pulses) */
.kpi{ font-size: 30px; font-weight: 800; color: var(--color-text-primary); line-height:1; font-family: var(--font-mono); }

/* Pills */
.pill{
  display: inline-flex; align-items:center; border-radius: var(--radius-pill);
  font-size: 11px; font-weight: 700; padding: 4px 10px;
}
.pill--cap{ text-transform: capitalize; }

/* Pipeline (order stages) */
.pipeline{ display:flex; flex-direction:column; }
.pipeline__row{ display:flex; align-items:center; gap:11px; padding:9px 0; border-top:1px solid var(--color-border-soft); }
.pipeline__row:first-child{ border-top:none; }
.pipeline__dot{ width:8px; height:8px; border-radius:50%; flex:0 0 8px; }
.pipeline__label{ font-size:12.5px; font-weight:600; color: var(--color-text-primary); flex:1; text-transform: capitalize; }
.pipeline__track{ width:64px; height:5px; border-radius:3px; background: var(--color-surface-2); overflow:hidden; }
.pipeline__fill{ height:100%; border-radius:3px; }
.pipeline__val{ font-size:13px; font-weight:800; font-family: var(--font-mono); width:24px; text-align:right; color: var(--color-text-primary); }

/* Grid System */
.grid{ display:grid; gap: 16px; }
.grid--four{ grid-template-columns: repeat(1, minmax(0,1fr)); }
.grid--three{ grid-template-columns: repeat(1, minmax(0,1fr)); }
.grid--two{ grid-template-columns: 1fr; }
.grid--3-1{ grid-template-columns: 1fr; }
.grid--1-2{ grid-template-columns: 1fr; }

@media (min-width: 768px){
  .grid--four{ grid-template-columns: repeat(2, minmax(0,1fr)); }
  .grid--three{ grid-template-columns: repeat(3, minmax(0,1fr)); }
  .grid--two{ grid-template-columns: 1fr 1fr; }
  .grid--3-1{ grid-template-columns: 1.6fr 1fr; }
  .grid--1-2{ grid-template-columns: 1fr 1.3fr; }
}
@media (min-width: 1200px){
  .grid--four{ grid-template-columns: repeat(4, minmax(0,1fr)); }
}

/* Charts */
.chart-h{ height: 260px; position: relative; }

/* Tables (scoped class so the global template .table CSS can't override) */
.table-wrapper{ overflow:auto; }
.dash-table{ width:100%; border-collapse: separate; border-spacing:0; min-width: 520px; }
.dash-table thead th{
  text-align:left; font-size: 10.5px; font-weight:700; letter-spacing:.05em; text-transform: uppercase; color: var(--color-text-tertiary);
  background: var(--color-surface-2); padding: 9px 18px; border: none; border-bottom: 1px solid var(--color-border-soft);
}
.dash-table tbody td{
  padding: 13px 18px; border: none; border-top: 1px solid var(--color-border-soft); color: var(--color-text-primary); font-size:13px; vertical-align: middle; background: transparent;
}
.dash-table tbody tr:hover td{ background: var(--color-hover); }
.dash-table .empty{ text-align:center; padding: 28px; color: var(--color-text-secondary); }
.bold{ font-weight: 700; color: var(--color-text-primary); font-family: var(--font-mono); }
.muted{ color: var(--color-text-secondary); }
.capitalize{ text-transform: capitalize; }

/* identity cell (avatar + name + email) */
.idcell{ display:flex; align-items:center; gap:10px; min-width:0; }
.idcell__avatar{ width:30px; height:30px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex:0 0 30px; }
.idcell__name{ font-size:12.5px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.idcell__sub{ font-size:11px; color: var(--color-text-tertiary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ts{ font-size:10.5px; color: var(--color-text-tertiary); font-family: var(--font-mono); }

/* Donut center */
.donut-wrap{ padding:20px 18px; display:flex; align-items:center; gap:22px; flex-wrap:wrap; }
.donut-legend{ flex:1; min-width:140px; display:flex; flex-direction:column; gap:12px; }
.donut-legend__row{ display:flex; align-items:center; gap:9px; }
.donut-legend__row i{ width:10px; height:10px; border-radius:3px; }
.donut-legend__label{ font-size:12.5px; font-weight:600; color: var(--color-text-primary); flex:1; text-transform: capitalize; }
.donut-legend__val{ font-size:12px; font-weight:800; font-family: var(--font-mono); color: var(--color-text-primary); }
/* CSS donut (conic) */
.css-donut{ position:relative; width:140px; height:140px; flex:0 0 140px; border-radius:50%; }
.css-donut__hole{ position:absolute; inset:26px; border-radius:50%; background: var(--color-surface); display:flex; flex-direction:column; align-items:center; justify-content:center; }
.css-donut__hole .num{ font-size:22px; font-weight:800; font-family: var(--font-mono); color: var(--color-text-primary); }
.css-donut__hole .lbl{ font-size:10px; color: var(--color-text-tertiary); font-weight:600; }
/* CSS vertical bars (orders by status) */
.bars{ display:flex; align-items:flex-end; justify-content:space-around; gap:14px; height:178px; padding:14px 0 0; }
.bars__col{ display:flex; flex-direction:column; align-items:center; justify-content:flex-end; gap:8px; flex:1; height:100%; }
.bars__val{ font-size:12px; font-weight:800; font-family: var(--font-mono); }
.bars__bar{ width:100%; max-width:46px; border-radius:8px 8px 3px 3px; transform-origin:bottom; animation: dashGrowBar .7s ease; }
.bars__label{ font-size:10px; font-weight:600; color: var(--color-text-secondary); text-align:center; text-transform:capitalize; }

/* Lists */
.list{ list-style:none; margin:0; padding:0; }
.list__row{ display:flex; align-items:center; justify-content:space-between; padding: 9px 0; border-top: 1px solid var(--color-border-soft); }
.list__row:first-child{ border-top: 0; }
.list__label{ text-transform: capitalize; font-size:12.5px; font-weight:600; color: var(--color-text-primary); }

/* Footer */
.footer{ text-align:center; font-size: 11.5px; color: var(--color-text-tertiary); padding: 26px 0 8px; }

/* Alerts */
.alert{ border-radius: var(--radius-md); padding: 14px 16px; border: 1px solid var(--color-danger-text); }
.alert--danger{ background: var(--color-danger-bg); border-color: var(--color-danger-text); color: var(--color-danger-text); }
.alert__title{ margin:0 0 6px; font-weight: 700; }
.alert__text{ margin:0; opacity:.9; }

/* Skeletons */
.skeleton{ background: linear-gradient(90deg, var(--color-hover) 25%, var(--color-surface-2) 37%, var(--color-hover) 63%); background-size: 400% 100%; animation: shimmer 1.3s infinite; border-radius: var(--radius-card); }
.skeleton__title{ width: 220px; height: 30px; margin: 4px 0 22px; }
.skeleton__card{ height: 150px; }
.skeleton__panel{ height: 300px; }
.mt-24{ margin-top: 16px; }
@keyframes shimmer{ 0%{ background-position: 100% 0; } 100%{ background-position: -100% 0; } }
      `}</style>
  );
}
